import React, { Component } from 'react';
import { compose, Query } from '../../reactApolloCompat';
import { withRouter } from '../../util/withRouter';
import withStyles from '@mui/styles/withStyles';
import { withErrorBoundary } from '@sentry/react';
import DataFormView from '../../component/form/v2/DataFormView';
import { PAGE_STOCK } from '../../util/strings';
import ErrorFallback from '../../component/ErrorFallback';
import Product from '../fragments/Product';
import { joinValidationResults, messages, validationHelper } from '../../util/validation';
import { isNullOrUndefined } from '../../util/objects';
import StockItemView from './StockItemView';
import Grid from '../../component/form/Grid';
import Paper from '@mui/material/Paper';
import Typography from '../../component/Typography';
import gql from 'graphql-tag';
import StockItemVariations from './StockItemVariations';
import BiggerMessageBar from '../../component/BiggerMessageBar';
import StockItemSales from './StockItemPricing';
import { isRelatedObjectUndefined } from '../../util/graphql';
import GatedComponent from '../../component/GatedComponent';

class StockItem extends Component {
    render() {
        const { match, classes } = this.props;
        const query = gql`
            query ReadOneProductHeader($id: ID!) {
                readOneProduct(ID: $id) {
                    ID
                    Title
                    InternalItemID
                    AllowPurchase
                }
            }
        `;
        return (
            <div>
                <Grid container spacing={3}>
                    <Query query={query} variables={{ id: match.params.id }}>
                        {({ data }) => {
                            const { readOneProduct } = data || {};
                            return this.renderHeader(readOneProduct);
                        }}
                    </Query>
                </Grid>
                <Paper className={classes.paper} elevation={0}>
                    <GatedComponent isEnabledCode={'CMS_ACCESS_SilverShop\\Admin\\ProductCatalogAdmin'}>
                        {isEnabled => (
                            <DataFormView
                                saveMode={1} // 1 = store/save all tabs that use the same fragment
                                tabs={StockTabs}
                                selectedIndex={match.params.key && match.params.key.indexOf('-') > 0 ? 2 : 0}
                                createNew={parseInt(match.params.id) === 0}
                                createNewFunc={() => {
                                    return {
                                        ID: null,
                                        InternalItemID: null
                                    };
                                }}
                                validation={StockValidation}
                                objectType="Product"
                                itemId={match.params.id}
                                name="Product"
                                context={this}
                                onCreated={e => this.onCreateNew(e)}
                                readOnly={!isEnabled}
                            />
                        )}
                    </GatedComponent>
                </Paper>
            </div>
        );
    }

    renderHeader(data) {
        const { classes } = this.props;
        const { InternalItemID, Title, AllowPurchase } = data || {};
        const isLoading = undefined === data;
        return (
            <Grid item xs={12} sm={12}>
                <div className="inline">
                    {false === AllowPurchase && (
                        <BiggerMessageBar messageType="error">This item is retired.</BiggerMessageBar>
                    )}
                    <Typography variant="display4" gutterBottom className={classes.pageTitle}>
                        Catalogue {InternalItemID}
                        <div className={classes.keyline} />
                        <small className={classes.header}>{Title || (isLoading ? 'Loading...' : 'New Item')}</small>
                    </Typography>
                </div>
            </Grid>
        );
    }

    onCreateNew(obj) {
        const { history } = this.props;
        history.push(PAGE_STOCK + obj.ID);
    }
}

const StockValidation = {
    stockOverview: {
        required: ['InternalItemID', 'Title', 'Parent.ID'],
        suggested: ['Content']
    },
    stockSales: {
        onValidate: {
            XeroCostsCode: (newValue, oldValue, isSet, getField) => {
                const hasValue = !!newValue || (!!oldValue && isNullOrUndefined(newValue));
                if (hasValue) return validationHelper.ok();
                if (getField('PurchasePrice') > 0) {
                    return validationHelper.required();
                }
                return validationHelper.ok();
            },
            XeroSalesCode: (newValue, oldValue, isSet, getField) => {
                const hasValue = !!newValue || (!!oldValue && isNullOrUndefined(newValue));
                if (hasValue) return validationHelper.ok();
                if (getField('SellingPrice') > 0) {
                    return validationHelper.required();
                }
                return validationHelper.ok();
            }
        }
    },
    stockVariations: {
        onValidate: {
            Variations: (newValue, persistedValue, hasValue, getField) => {
                const validationResults = [];
                (newValue || persistedValue || []).forEach((variation, index) => {
                    const invalidFields = [];
                    variation.AttributeValues
                        ? variation.AttributeValues.forEach((obj, idx) => {
                              if (isRelatedObjectUndefined(obj)) {
                                  invalidFields.push(`Variations[${index}].AttributeValues[${idx}]`);
                              }
                          })
                        : invalidFields.push(`Variations[${index}].AttributeValues[0]`);

                    if (invalidFields.length > 0)
                        validationResults.push(validationHelper.required(messages.required, invalidFields));
                });

                return joinValidationResults(validationResults);
            }
        }
    }
};

const reduceOverridesOnLoad = data => {
    return data.PriceOverrides.edges.map(({ node }) => ({
        ID: node.ID, // OfficeID
        BasePrice: node._join.PriceOverride.BasePrice,
        SellingPrice: node._join.PriceOverride.SellingPrice
    }));
};
const reduceOverridesOnSave = data => {
    return data.PriceOverrides.map(({ SellingPrice, ...obj }) => obj).filter(obj => !!obj.ID); // drop when no office selected
};
let pricesCount = 0;
const setCountPrices = len => {
    pricesCount = len;
};
let variationsCount = 0;
const setCountVariations = len => {
    variationsCount = len;
};
const StockTabs = [
    {
        id: 'stockOverview',
        label: 'Overview',
        component: StockItemView,
        fragment: Product,
        onLoad: () => null,
        formatSaveData: (saveData, state) => {
            if (saveData.ProductCategories) {
                saveData.ProductCategories = state.ProductCategories.map(obj => ({ ID: obj.ID }));
            }

            if (saveData.Parent && saveData.Parent.ID > 0) {
                saveData.ParentID = saveData.Parent.ID;
                delete saveData.Parent;
            }

            delete saveData.Image;
            saveData.ImageURL = null;
        }
    },
    {
        id: 'stockSales',
        get label() {
            return (pricesCount > 0 ? pricesCount + 1 : '') + ' Prices';
        },
        component: StockItemSales,
        fragment: Product,
        onLoad: data => {
            if (data.PriceOverrides) {
                data.PriceOverrides = reduceOverridesOnLoad(data);
                setCountPrices(data.PriceOverrides.length);
            }
        },
        formatSaveData: (saveData, state) => {
            saveData.PurchasePrice = '';
            saveData.SellingPrice = '';
            if (saveData.PriceOverrides) {
                saveData.PriceOverrides = reduceOverridesOnSave(state);
            }
        }
    },
    {
        id: 'stockVariations',
        get label() {
            return (variationsCount || 'No') + ' Variations';
        },
        component: StockItemVariations,
        fragment: Product,
        onLoad: data => {
            if (data.Variations) {
                data.Variations = data.Variations.edges.map(({ node }) => ({
                    ...node,
                    AttributeValues: node.AttributeValues.edges.map(({ node: { Type, ...att } }) => ({
                        ...att,
                        TypeID: Type.ID
                    })),
                    PriceOverrides: reduceOverridesOnLoad(node)
                }));
                setCountVariations(data.Variations.length);
            }
            if (data.VariationAttributeTypes) {
                data.VariationAttributeTypes = data.VariationAttributeTypes.nodes.map(node => ({
                    ID: node.ID
                }));
            }
        },
        formatSaveData: (saveData, state) => {
            if (saveData.Variations) {
                saveData.Variations = state.Variations.map(
                    ({ PriceOverrides, SellingPrice, PurchasePrice, ...node }) => ({
                        ...node,
                        PriceOverrides: reduceOverridesOnSave(state)
                    })
                );
            }
        }
    }
];

const styles = ({ spacing, typography, palette, breakpoints, funeralHome }) => ({
    paper: {
        padding: `0 ${spacing(1)}`
    },
    pageTitle: {
        display: 'flex',
        flexGrow: 1,
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px 16px',
        color: palette.contentForeground[funeralHome]
    },
    keyline: {
        width: 1,
        height: 30,
        display: 'inline-block',
        opacity: 0.5,
        backgroundColor: palette.action.active
    },
    header: {
        fontWeight: typography.fontWeightLight,
        color: palette.text.primary
    }
});
export default withErrorBoundary(compose(withRouter, withStyles(styles))(StockItem), { fallback: ErrorFallback });
